import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import {
  ActionTakenType,
  DocumentTemplateType,
  INumberedDocumentView,
  WorkflowStatusType,
} from '../../api-client/autogenerated';
import { getDocumentsByProjectId } from '../../models/api/project';
import {
  getDisableProcoreIntegrationFeatures,
  getProjectState,
} from '../../features/project/selectors';
import { ARCHIVED_PROJECT_TOOLTIP, isProjectArchived } from '../../scripts/permission-utils';
import {
  getNextNumberInRequirement,
  latestSubmittalPerRequirement,
} from '../../scripts/submittal-requirement-utils';
import { getDisplayDivision } from '../document-display/DocumentDisplay';
import { GreenCheck } from '../custom-components/CustomCheckboxes';
import { CancelButton, TooltipIfDisabledComponent } from '../custom-components/CustomButtons';
import CircularLoader from '../loader/CircularLoader';

/**
 * Only what this dialog shows. A project's submittals run into the high hundreds, and asking for
 * every column of all of them is enough to time the request out.
 *
 * These are the stored columns. `submittalDivision` and `submittalExtendedIdentifier` cannot be
 * asked for -- they are not on numbered_document_view, they are derived on the Document model from
 * the section, paragraph, subparagraph and revision below, and come back with the response.
 */
const SUBMITTAL_COLUMNS: (keyof INumberedDocumentView)[] = [
  'id',
  'title',
  'workflowStatus',
  'actionTaken',
  'submittalPackageDocumentId',
  'submittalSection',
  'submittalParagraphNumber',
  'submittalSubparagraphNumber',
  'revisionNumber',
  'submittalSectionDescription',
  'submittalDivisionDescription',
  'createdOn',
];

/**
 * The statuses a submittal can be packaged from, copied from the effect in DocumentIndex that feeds
 * DocumentIndexToolbar's disableCreatePackage. It carries a TODO to narrow this back to Initial
 * alone once subcontractor support lands, so it is worth changing in both places together.
 *
 * Narrower than the "Awaiting Submission" the logs display: SubcontractorUploaded reads as that
 * through statusToGeneralString but cannot be packaged, so the rule is on the status, never on the
 * string.
 */
const PACKAGEABLE_STATUSES = [
  WorkflowStatusType.Initial,
  WorkflowStatusType.ReadyForSubmissionToArchitect,
];

/** A package of one is just the submittal, which is what the log page's toolbar requires too. */
const MINIMUM_SUBMITTALS = 2;

/**
 * How a row names its requirement. Deliberately not submittalExtendedIdentifier, which carries the
 * subparagraph and revision: which attempt at the requirement this happens to be is not the user's
 * choice to make here, so it is not put in front of them.
 */
const requirementIdentifier = (submittal: INumberedDocumentView) =>
  [submittal.submittalSection, submittal.submittalParagraphNumber].filter(Boolean).join('-') ||
  'N/A';

/**
 * Whether a package cannot simply take this submittal as it stands -- it is in one already, was
 * withdrawn, or has moved past the point a package is built from. The requirement still belongs in
 * the package, so a fresh submittal is created against it rather than the selection being refused.
 */
const needsFreshSubmittal = (submittal: INumberedDocumentView) =>
  !!submittal.submittalPackageDocumentId ||
  submittal.actionTaken === ActionTakenType.Withdrawn ||
  (!!submittal.workflowStatus && !PACKAGEABLE_STATUSES.includes(submittal.workflowStatus));

/** A submittal to create against an existing one's requirement, for a package to be built from. */
export type SubmittalToCreate = {
  title?: string;
  submittalSection?: string;
  submittalParagraphNumber?: string;
  submittalSubparagraphNumber?: string;
  submittalSectionDescription?: string;
};

/** One requirement the user ticked, and what the package takes for it. */
export type SubmittalPackageSelection = {
  /** Identifies the row wherever the package's contents are named back to the user. */
  key: string;
  label: string;
  /** The submittal to add, when it can go into a package as it stands. */
  documentId?: string;
  /** A fresh submittal against the same requirement, created with the package instead. */
  create?: SubmittalToCreate;
};

const useStyles = makeStyles({
  divisionRow: {
    borderBottom: '1px solid #EDECEC',
  },
  // MuiListItemText's theme colour is tuned for the dark navigation drawer, so these rows use
  // Typography with the ordinary body colour, as the other submittal dialogs do.
  rowText: {
    color: '#464546', // Gray 700
    flexGrow: 1,
  },
  identifier: {
    color: '#464546', // Gray 700
    fontWeight: 500,
    minWidth: 110,
    paddingRight: 12,
  },
  divisionHeading: {
    color: '#464546', // Gray 700
    fontWeight: 500,
    fontSize: 18,
  },
  backButton: {
    textTransform: 'none',
    padding: 0,
    minWidth: 0,
    marginBottom: 4,
  },
  empty: {
    color: '#949494', // Gray 400
    marginTop: 12,
  },
});

type Props = {
  open: boolean;
  projectId: string;
  /**
   * Which log the package belongs to -- Submittals or Closeout Submittals. It decides which
   * documents the picker gathers and how it names them; AddPackageDialog reads the same type out
   * of redux to decide which kind of package to make.
   */
  documentType: DocumentTemplateType;
  /**
   * Whether the package these submittals are being gathered into is a placeholder for a contractor
   * to fill in later. It decides the wording here, and is handed on to AddPackageDialog, which
   * requires a file of a package that is not one.
   */
  isPlaceholder: boolean;
  handleClose: () => void;
  /**
   * The requirements to gather, once there are enough of them and the log page's rules allow it.
   * Not just ids: this dialog closes behind the one that names the package, which lists them back
   * so the user can still see what they picked -- and one that cannot be packaged as it stands
   * carries the fresh submittal to create for it instead of a document to add.
   */
  onCreatePackage: (selections: SubmittalPackageSelection[]) => void;
};

/**
 * Gathering the submittals a package is made of, a division at a time.
 *
 * A package is only ever built from submittals that already exist, and a project carries hundreds
 * of them, so they are reached the way the log page reaches them: choose the division first, then
 * tick the submittals within it. Confining the choice to one division is also what satisfies the
 * log page's same-division requirement without having to check it.
 */
export default function SelectSubmittalsDialog(props: Props) {
  const classes = useStyles();
  const { open, projectId, documentType, isPlaceholder, handleClose, onCreatePackage } = props;

  /** What this log calls the documents it holds, for the dialog's own wording. */
  const typeWord =
    documentType === DocumentTemplateType.CloseoutSubmittals ? 'Closeout Submittal' : 'Submittal';

  const project = useSelector(getProjectState);
  const disableProcoreIntegrationFeatures = useSelector(getDisableProcoreIntegrationFeatures);

  const [isLoading, setIsLoading] = useState(true);
  const [submittals, setSubmittals] = useState<INumberedDocumentView[]>([]);
  const [division, setDivision] = useState<string>();
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (!open || !projectId) return;
    setIsLoading(true);
    setDivision(undefined);
    setSelected([]);
    getDocumentsByProjectId(projectId, SUBMITTAL_COLUMNS, [documentType])
      .then(setSubmittals)
      .catch(() => setSubmittals([]))
      .finally(() => setIsLoading(false));
  }, [open, projectId, documentType]);

  // Only the divisions this project actually has submittals in, as the log page's index lists them.
  const divisions = useMemo(
    () =>
      Array.from(
        new Set(submittals.map((submittal) => submittal.submittalDivision).filter(Boolean)),
      ).sort() as string[],
    [submittals],
  );

  /**
   * What the division offers: its requirements, each represented by its most recent submittal, in
   * the order their numbers put them -- which is the order the submittal log lists them in, and
   * the order somebody reading a specification looks for them. A requirement met three times over
   * is one thing to put in a package, not three.
   */
  const submittalsInDivision = useMemo(
    () =>
      latestSubmittalPerRequirement(
        submittals.filter((submittal) => submittal.submittalDivision === division),
      ).sort((a, b) =>
        // Numeric so 10 follows 9 rather than 1, since these are numbers written as text.
        requirementIdentifier(a).localeCompare(requirementIdentifier(b), undefined, {
          numeric: true,
        }),
      ),
    [submittals, division],
  );

  const selectedSubmittals = useMemo(
    () => submittalsInDivision.filter((submittal) => selected.includes(submittal.id)),
    [submittalsInDivision, selected],
  );

  const toggle = (documentId: string) =>
    setSelected((previous) =>
      previous.includes(documentId)
        ? previous.filter((id) => id !== documentId)
        : [...previous, documentId],
    );

  const packageWord = isPlaceholder ? 'Create Package Placeholder' : 'Create Package';

  /**
   * What the package takes for each ticked requirement: the submittal itself where a package can
   * be built from it, and otherwise a fresh submittal numbered into the same requirement -- the
   * same thing the "Create a New Submittal For This Requirement" button on a submittal's own page
   * produces.
   */
  const getSelections = (): SubmittalPackageSelection[] =>
    selectedSubmittals.map((submittal) => {
      const label = [requirementIdentifier(submittal), submittal.title].filter(Boolean).join('  ');
      if (!needsFreshSubmittal(submittal))
        return { key: submittal.id, label, documentId: submittal.id };
      return {
        key: submittal.id,
        label,
        create: {
          title: submittal.title || undefined,
          submittalSection: submittal.submittalSection || undefined,
          submittalSectionDescription: submittal.submittalSectionDescription || undefined,
          ...getNextNumberInRequirement(submittal, submittals),
        },
      };
    });

  /**
   * What the log page requires before it will let a package be created, in one place.
   *
   * The rule is split there: DocumentIndex's effect decides the document conditions and hands them
   * to DocumentIndexToolbar as disableCreatePackage, which adds the project-level ones. Both halves
   * are mirrored here so the dashboard cannot offer a package the log page would refuse.
   *
   * Two of its conditions are not repeated. Every submittal here belongs to the division being
   * browsed, so the same-division requirement holds by construction; and a package is a document of
   * another type, so one can never appear in this list to be selected.
   *
   * Permission is not re-checked either -- which of the two buttons opened this dialog is already
   * the split the toolbar draws between createPackagePermission and createPlaceholderPackagePermission.
   */
  const getPackageBlockedReason = () => {
    if (isProjectArchived(project)) return ARCHIVED_PROJECT_TOOLTIP;
    if (disableProcoreIntegrationFeatures) return 'Currently Syncing with Procore';
    if (selected.length < MINIMUM_SUBMITTALS)
      return `Select at least ${MINIMUM_SUBMITTALS} submittals to gather into a package.`;
    // Nothing about the submittals themselves blocks a package any more: one that cannot be
    // packaged as it stands has a fresh submittal created against its requirement instead.
    return undefined;
  };

  const packageBlockedReason = getPackageBlockedReason();

  const renderDivisions = () => (
    <>
      <Typography style={{ marginBottom: 8 }}>
        Choose the division the submittals belong to. A package gathers submittals from one
        division.
      </Typography>
      {divisions.length === 0 ? (
        <Typography className={classes.empty}>
          This project has no submittals to gather into a package yet.
        </Typography>
      ) : (
        <List dense style={{ maxHeight: 420, overflowY: 'auto' }}>
          {divisions.map((value) => (
            <ListItem
              key={value}
              button
              className={classes.divisionRow}
              onClick={() => {
                setDivision(value);
                setSelected([]);
              }}
            >
              <Typography className={classes.rowText}>{getDisplayDivision(value)}</Typography>
              <ChevronRightIcon htmlColor="#949494" />
            </ListItem>
          ))}
        </List>
      )}
    </>
  );

  const renderSubmittals = () => (
    <>
      <Button
        className={classes.backButton}
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={() => {
          setDivision(undefined);
          setSelected([]);
        }}
      >
        All divisions
      </Button>
      <Typography className={classes.divisionHeading}>{getDisplayDivision(division!)}</Typography>
      <Divider style={{ marginTop: 8 }} />
      {submittalsInDivision.length === 0 ? (
        <Typography className={classes.empty}>There are no submittals in this division.</Typography>
      ) : (
        <List dense style={{ maxHeight: 380, overflowY: 'auto' }}>
          {submittalsInDivision.map((submittal) => (
            <ListItem key={submittal.id} button onClick={() => toggle(submittal.id)}>
              <ListItemIcon style={{ minWidth: 36 }}>
                {/*
                  The row owns the toggle. Giving the checkbox its own handler too means a click on
                  the box itself runs both and lands back where it started.
                */}
                <GreenCheck checked={selected.includes(submittal.id)} tabIndex={-1} disableRipple />
              </ListItemIcon>
              <Typography className={classes.identifier}>
                {requirementIdentifier(submittal)}
              </Typography>
              {/* The submittal log's own Description column, to the letter: the section's
                  description in bold above the submittal's own. */}
              <div className={classes.rowText}>
                {submittal.submittalSectionDescription ? (
                  <Typography style={{ fontWeight: 700, fontSize: 16 }}>
                    {submittal.submittalSectionDescription}
                  </Typography>
                ) : null}
                <Typography style={{ fontWeight: 'normal', fontSize: 15 }}>
                  {submittal.title || ''}
                </Typography>
              </div>
            </ListItem>
          ))}
        </List>
      )}
    </>
  );

  return (
    <Dialog open={open} fullWidth PaperProps={{ style: { maxWidth: 780 } }} onClose={handleClose}>
      <DialogTitle disableTypography>
        <Typography variant="h1" style={{ textTransform: 'none', lineHeight: 1 }}>
          {isPlaceholder ? `Create ${typeWord} Package Placeholder` : `Upload ${typeWord} Package`}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {isLoading ? <CircularLoader /> : division ? renderSubmittals() : renderDivisions()}
      </DialogContent>
      {/*
        Both buttons are always here, Cancel pinned left and the action pinned right, so that
        ticking a submittal changes what the action says and whether it is live -- never where
        either button sits.
      */}
      <DialogActions style={{ padding: 16, justifyContent: 'space-between' }}>
        <CancelButton onClick={handleClose} />
        <TooltipIfDisabledComponent
          disabled={!!packageBlockedReason}
          title={packageBlockedReason ?? ''}
          // The wrapper's default is a full-width span, which stretched the action across the
          // dialog the moment it went dead -- so the pair only looked pinned apart while it was live.
          spanStyle={{ width: 'auto' }}
        >
          <Button
            variant="contained"
            color="primary"
            disableElevation
            disabled={!!packageBlockedReason}
            onClick={() => onCreatePackage(getSelections())}
          >
            {packageWord}
          </Button>
        </TooltipIfDisabledComponent>
      </DialogActions>
    </Dialog>
  );
}
